import {
  cloudRunViewerShell,
  cloudRunViewerTf,
} from "@p0security/integrations-gcloud-shared/cloud-run-iac";
import type { GcloudIntegration } from "@p0security/integrations-gcloud-shared/types";
import type { GcpCloudSqlComponents } from "@p0security/integrations-gcp-cloudsql-shared/components";
import { integrationOptionProvider } from "@p0security/integrations-installer";
import type { FrontendInstaller } from "@p0security/integrations-installer";
import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import type { Instructions } from "@p0security/shared/install/types";
import { Typography } from "@p0security/ui";
import objectHash from "object-hash";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

export type GcpCloudSqlInstaller = FrontendInstaller<
  typeof GcpCloudSqlComponents
>;

const { Paragraph, Text } = Typography;

const hashId = (id: string) => objectHash({ id }).slice(0, 8);

/**
 * Must match the image that the Terraform tab's module version pins; for v0.1.0 that is
 * https://github.com/p0-security/terraform-google-p0-connector/blob/v0.1.0/main.tf#L13
 */
const CONNECTOR_IMAGE =
  "docker.io/p0security/p0-connector-cloudsql:sha-49c310f@sha256:cdcbcbd778fbc752cc3144fff665f569f0d56bfb4b4e104e9d88a0b33b37f5c9";

/** Permissions the connector uses to list and create IAM database users. Login
 * comes separately from roles/cloudsql.instanceUser. */
const CONNECTOR_ROLE_PERMISSIONS = [
  "cloudsql.instances.get",
  "cloudsql.users.create",
  "cloudsql.users.list",
  "resourcemanager.projects.get",
];

const gcpCloudSqlIamWriteInstructions: GcpCloudSqlInstaller["components"]["iam-write"]["instructions"] =
  (context, id, item): Instructions<ReactNode> => {
    const {
      projectId,
      connectorRegion,
      connectorSubnetwork,
      connectorServiceName,
      connectorServiceAccount,
    } = item;
    const gcloud = context.installed.gcloud as GcloudIntegration | undefined;
    const invokerServiceAccount =
      gcloud?.root?.[SINGLETON_ITEM]?.serviceAccountEmail;
    const connectorServiceAccountName =
      connectorServiceAccount?.split("@")[0] ?? "";
    const tfId = hashId(id);
    // One role per connector, so several VPCs in a project don't collide.
    // Custom role IDs can't contain hyphens.
    const connectorRoleId = connectorServiceAccountName.replace(/-/g, "_");
    const connectorRole = `projects/${projectId}/roles/${connectorRoleId}`;

    // The p0-connector module does not grant roles/run.viewer yet; CUS-95 adds
    // it there and removes this.
    const connectorViewerTf = cloudRunViewerTf({
      resource: `p0_cloudsql_connector_viewer_${tfId}`,
      project: `"${projectId}"`,
      location: `"${connectorRegion}"`,
      service: `module.gcp_cloudsql_vpc_${tfId}.service_name`,
      member: invokerServiceAccount ?? "",
    });

    const vpcAccessIntro = (
      <>
        <Paragraph>
          The P0 connector for GCP CloudSQL is deployed as a Cloud Run service,
          and uses direct VPC egress to connect to VPC-bound instances.
        </Paragraph>
        <Paragraph>
          First, ensure GCP Cloud Run has access to the VPC. Read the{" "}
          <Link
            to={
              "https://docs.cloud.google.com/run/docs/configuring/vpc-direct-vpc#direct-vpc-iam"
            }
            target="_blank"
            rel="noreferrer"
          >
            Cloud Run documentation
          </Link>{" "}
          for more information to determine if any action is required. Note that
          by default, the Cloud Run service agent is already configured with the
          necessary permissions, so GCP Cloud Run may already have VPC access.
        </Paragraph>
      </>
    );

    return {
      commands: {
        shell: [
          {
            header: (
              <>
                {vpcAccessIntro}
                <Paragraph>
                  Then, create the connector&apos;s service account:
                </Paragraph>
              </>
            ),
            command: `gcloud iam service-accounts create ${connectorServiceAccountName} \\
  --project=${projectId} \\
  --display-name="P0 Cloud Run cloudsql connector" \\
  --description="Service account used by the P0 Cloud Run connector for CloudSQL in VPC ${id}"
`,
          },
          {
            header: (
              <>
                <Paragraph>
                  Grant the service account its CloudSQL roles.
                </Paragraph>
                <Paragraph>
                  A new service account can take a minute or more to become
                  visible to IAM. If a binding fails with &quot;Service account
                  ... does not exist&quot;, wait a minute and run it again.
                </Paragraph>
              </>
            ),
            command: `gcloud iam roles create ${connectorRoleId} \\
  --project=${projectId} \\
  --stage=GA \\
  --title="P0 CloudSQL connector" \\
  --description="Lets the P0 CloudSQL connector in VPC ${id} create IAM database users" \\
  --permissions=${CONNECTOR_ROLE_PERMISSIONS.join(",")}

gcloud projects add-iam-policy-binding ${projectId} \\
  --member="serviceAccount:${connectorServiceAccount}" \\
  --condition=None \\
  --role="${connectorRole}"

gcloud projects add-iam-policy-binding ${projectId} \\
  --member="serviceAccount:${connectorServiceAccount}" \\
  --condition=None \\
  --role="roles/cloudsql.instanceUser"
`,
          },
          {
            header: <Paragraph>Then, deploy the connector:</Paragraph>,
            command: `gcloud run deploy ${connectorServiceName} \\
  --project=${projectId} \\
  --region=${connectorRegion} \\
  --image=${CONNECTOR_IMAGE} \\
  --service-account=${connectorServiceAccount} \\
  --set-env-vars=INVOKER_SA_EMAIL=${invokerServiceAccount ?? ""} \\
  --network=${id} \\
  --subnet=${connectorSubnetwork} \\
  --vpc-egress=private-ranges-only \\
  --ingress=all \\
  --no-allow-unauthenticated
`,
          },
          {
            header: (
              <Paragraph>
                Finally, let P0&apos;s service account{" "}
                <Text code>{invokerServiceAccount}</Text> read and invoke the
                connector, then click &quot;Next&quot;:
              </Paragraph>
            ),
            command: `gcloud run services add-iam-policy-binding ${connectorServiceName} \\
  --project=${projectId} \\
  --region=${connectorRegion} \\
  --member="serviceAccount:${invokerServiceAccount ?? ""}" \\
  --role="roles/run.invoker"

${cloudRunViewerShell({
  service: connectorServiceName,
  projectId,
  region: connectorRegion,
  member: invokerServiceAccount ?? "",
})}
`,
          },
        ],
        iac: [
          {
            header: (
              <>
                {vpcAccessIntro}
                <Paragraph>
                  P0&apos;s service account{" "}
                  <Text code>{invokerServiceAccount}</Text> needs{" "}
                  <Text code>roles/run.viewer</Text> to look up the
                  connector&apos;s URL.
                </Paragraph>
                <Paragraph>
                  Then, add the following to your terraform, then apply, the
                  click &quot;Next&quot;:
                </Paragraph>
              </>
            ),
            command: `
resource "google_project_iam_custom_role" "connector_cloudsql_${tfId}" {
  project     = "${projectId}"
  role_id     = "${connectorRoleId}"
  title       = "P0 CloudSQL connector"
  description = "Lets the P0 CloudSQL connector in VPC ${id} create IAM database users"
  permissions = [
    ${CONNECTOR_ROLE_PERMISSIONS.map((p) => `"${p}"`).join(",\n    ")}
  ]
}

resource "google_project_iam_member" "connector_cloudsql_users_${tfId}" {
  project = "${projectId}"
  role    = google_project_iam_custom_role.connector_cloudsql_${tfId}.name
  member  = "serviceAccount:${connectorServiceAccount}"
}            
            
module "gcp_cloudsql_vpc_${tfId}" {
  source                          = "p0-security/p0-connector/google"
  version                         = "0.1.0"

  project_id                      = "${projectId}"
  service                         = "cloudsql"
  connector_name                  = "${connectorServiceName}"
  connector_service_account_name  = "${connectorServiceAccountName}"
  region                          = "${connectorRegion}"
  vpc_network                     = "${id}"
  vpc_subnetwork                  = "${connectorSubnetwork}"
  invoker_service_account_email   = "${invokerServiceAccount ?? ""}"
}

${connectorViewerTf}

resource "google_project_iam_member" "cloudsql_instance_user_${tfId}" {
  project = "${projectId}"
  role    = "roles/cloudsql.instanceUser"
  member  = "serviceAccount:${connectorServiceAccount}"
}
`,
          },
        ],
      },
    };
  };

export const gcpCloudSqlInstaller: GcpCloudSqlInstaller = {
  components: {
    "iam-write": {
      items: {
        projectId: {
          optionProvider: integrationOptionProvider("iam-write", [
            {
              component: "iam-write",
              integration: "gcloud",
            },
          ]),
        },
      },
      instructions: gcpCloudSqlIamWriteInstructions,
    },
  },
};
